import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { apiClient } from '@/lib/apiClient';
import { useHasPermission } from '@/components/PermissionGate';
import {
  useDimensionPrecedence,
  rungByRank,
  rungLabel,
  type DimensionPrecedenceRung,
} from './useDimensionPrecedence';

/**
 * PlanLineExplainDialog — the "where did this dimension come from?" surface.
 *
 * Fetches GET /api/finance/planline/line-explanation/{id}: a read-only replay of the derivation
 * pipeline for one stored PlanLine against the CURRENT configuration. Shows, per axis, which
 * merge-ladder source produced the value (cell pick > header pin > entity row key > carried
 * entity default > org-unit default chain), what it overrode, and the previewed set versus the
 * stored one — a mismatch means configuration changed after the line was saved.
 */

interface CandidateTrace {
  dimensionValueId: string;
  valueCode?: string | null;
  valueName?: string | null;
  source: string;
  sourceLabel?: string | null;
  /** PRODUCT_GROUP | CUSTOMER | TRADE_TYPE | SUPPLIER — set only for carried defaults. */
  carrier?: string | null;
  /** The candidate's rung on the merge ladder (1 = strongest); joins to the fetched ladder. */
  precedenceRank?: number;
}

interface AxisExplanation {
  dimensionDefinitionId: string;
  definitionCode: string;
  definitionName: string;
  winner?: CandidateTrace | null;
  overridden: CandidateTrace[];
  outcome: string;
}

interface PlanLineExplanation {
  planLineId: string;
  planHeaderId: string;
  fiscalPeriodId: string;
  accountId?: string | null;
  accountLabel?: string | null;
  accountSuppliedByRow: boolean;
  derivedStatus: string;
  derivedReason?: string | null;
  axes: AxisExplanation[];
  resolvedDimensionSetId?: string | null;
  resolvedDisplayValue?: string | null;
  storedDimensionSetId?: string | null;
  storedDisplayValue?: string | null;
  matchesStoredSet: boolean;
}

const SOURCE_KEYS: Record<string, { key: string; defaultValue: string }> = {
  CELL: { key: 'Finance.PlanLineExplain.SourceCell', defaultValue: 'Cell value (row / section pick)' },
  HEADER_PIN: { key: 'Finance.PlanLineExplain.SourceHeaderPin', defaultValue: 'Header scope pin' },
  ENTITY_ROW_KEY: { key: 'Finance.PlanLineExplain.SourceRowEntity', defaultValue: 'Row entity' },
  ENTITY_CARRIED_DEFAULT: { key: 'Finance.PlanLineExplain.SourceCarriedDefault', defaultValue: 'Carried entity default' },
  ORG_UNIT_DEFAULT: { key: 'Finance.PlanLineExplain.SourceOrgUnitDefault', defaultValue: 'Org unit default' },
};

/**
 * The source as a user reads it. Prefers the fetched ladder's rung label, which names the specific
 * carrier ("Default from the assortment") where the raw source enum cannot — two carried defaults
 * are the same enum value, and that is exactly the collision this dialog has to explain. Falls back
 * to the generic per-source wording while the ladder is loading.
 */
function sourceText(t: TFunction, trace: CandidateTrace, ladder?: DimensionPrecedenceRung[]): string {
  const rung = rungByRank(ladder, trace.precedenceRank);
  const cfg = SOURCE_KEYS[trace.source];
  const base = rung
    ? rungLabel(t, rung)
    : cfg
      ? t(cfg.key, { defaultValue: cfg.defaultValue })
      : trace.source;
  return trace.sourceLabel ? `${base} — ${trace.sourceLabel}` : base;
}

/**
 * The rung's tier label ("3", "4a") as a compact badge. Numbering the sources is what turns a list
 * of names into a visible order — without it a user has no way to see why one beat the other.
 */
function StepBadge({ step, dimmed }: { step: string; dimmed?: boolean }) {
  return (
    <Chip
      size="small"
      label={step}
      variant={dimmed ? 'outlined' : 'filled'}
      sx={{
        height: 18,
        minWidth: 26,
        mr: 0.75,
        fontSize: '0.7rem',
        fontVariantNumeric: 'tabular-nums',
        '& .MuiChip-label': { px: 0.5 },
        ...(dimmed ? { color: 'text.disabled', borderColor: 'divider' } : {}),
      }}
    />
  );
}

function valueText(t: TFunction, trace: CandidateTrace): string {
  if (!trace.valueCode && !trace.valueName) {
    return t('Finance.PlanLineExplain.ValueGone', { defaultValue: '(value no longer exists)' });
  }
  return trace.valueName ? `${trace.valueCode} – ${trace.valueName}` : trace.valueCode || '';
}

function OutcomeChip({ outcome }: { outcome: string }) {
  const { t } = useTranslation();
  switch (outcome) {
    case 'BLOCKED_BY_ACCOUNT_RULE':
      return (
        <Chip
          size="small"
          color="warning"
          label={t('Finance.PlanLineExplain.OutcomeBlocked', { defaultValue: 'Blocked by account rule' })}
        />
      );
    case 'VALUE_UNRESOLVABLE':
      return (
        <Chip
          size="small"
          color="error"
          label={t('Finance.PlanLineExplain.OutcomeUnresolvable', { defaultValue: 'Value no longer exists' })}
        />
      );
    case 'REQUIRED_MISSING':
      return (
        <Chip
          size="small"
          color="error"
          label={t('Finance.PlanLineExplain.OutcomeRequiredMissing', { defaultValue: 'Required — no value' })}
        />
      );
    default:
      return null; // INCLUDED needs no badge — it is the normal case
  }
}

/**
 * "How the value is chosen" — the whole merge ladder, strongest first, with the rungs that actually
 * contributed to THIS line highlighted. The per-axis table above answers "where did this value come
 * from?"; this answers the question that follows immediately, "and why did that one win?".
 *
 * Collapsed by default: it is the reference, not the answer.
 */
function PrecedenceLegend({
  ladder,
  usedRanks,
}: {
  ladder: DimensionPrecedenceRung[];
  usedRanks: Set<number>;
}) {
  const { t } = useTranslation();
  return (
    <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', '&::before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2">
          {t('Finance.PlanLineExplain.LadderTitle', { defaultValue: 'How the value is chosen' })}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('Finance.PlanLineExplain.LadderIntro', {
            defaultValue:
              'The first source that supplies a value for a dimension wins it. Sources are tried in this order:',
          })}
        </Typography>
        <Box component="ol" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
          {ladder.map((rung) => {
            const used = usedRanks.has(rung.rank);
            return (
              <Box
                component="li"
                key={rung.rank}
                sx={{ display: 'flex', alignItems: 'center', py: 0.25 }}
              >
                <StepBadge step={rung.step} dimmed={!used} />
                <Typography
                  variant="body2"
                  color={used ? 'text.primary' : 'text.secondary'}
                  sx={{ fontWeight: used ? 600 : 400 }}
                >
                  {rungLabel(t, rung)}
                </Typography>
              </Box>
            );
          })}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('Finance.PlanLineExplain.LadderFootnote', {
            defaultValue:
              'Steps 4a–4d only compete with each other when one row carries several records — for example an assortment and a trade type that both set a cost centre. Bold steps contributed to this line.',
          })}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

export interface PlanLineExplainDialogProps {
  entityId: string;
  open: boolean;
  onClose: () => void;
}

export function PlanLineExplainDialog({ entityId, open, onClose }: PlanLineExplainDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canEdit = useHasPermission('Finance.PlanEntry.Edit');
  const { data, isLoading, error } = useQuery({
    queryKey: ['plan-line-explanation', entityId],
    queryFn: async () =>
      (await apiClient.get<PlanLineExplanation>(`/api/finance/planline/line-explanation/${entityId}`)).data,
    enabled: open,
  });
  // The precedence rules, from the engine that applies them (never a copy in this file).
  const { data: ladder } = useDimensionPrecedence();

  // Which rungs actually contributed to this line — highlighted in the legend so the general rule
  // and this specific line are visibly the same story.
  const usedRanks = new Set<number>();
  for (const axis of data?.axes ?? []) {
    if (axis.winner?.precedenceRank != null) usedRanks.add(axis.winner.precedenceRank);
    for (const o of axis.overridden) {
      if (o.precedenceRank != null) usedRanks.add(o.precedenceRank);
    }
  }

  // The drift warning's follow-up: re-derive the stored line against the current configuration,
  // then refetch the explanation — a successful recompute flips it to "matches stored set".
  const recompute = useMutation({
    mutationFn: async () =>
      (await apiClient.post(`/api/finance/planline/recompute-line/${entityId}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan-line-explanation', entityId] });
    },
  });

  const none = t('Finance.PlanLineExplain.None', { defaultValue: '(none)' });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('Finance.PlanLineExplain.Title', { defaultValue: 'Explain dimension derivation' })}</DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t('Finance.PlanLineExplain.Loading', { defaultValue: 'Replaying derivation…' })}
            </Typography>
          </Box>
        )}

        {!!error && (
          <Alert severity="error">
            {t('Finance.PlanLineExplain.LoadFailed', { defaultValue: 'Failed to load the explanation.' })}
          </Alert>
        )}

        {data && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                color={data.derivedStatus === 'RESOLVED' ? 'success' : 'warning'}
                label={t(`Enum:MappingStatus:${data.derivedStatus}`, { defaultValue: data.derivedStatus })}
              />
              {data.derivedReason && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="warning"
                  label={t(`Enum:MappingReason:${data.derivedReason}`, { defaultValue: data.derivedReason })}
                />
              )}
              {data.accountLabel && (
                <Typography variant="body2" color="text.secondary">
                  {data.accountSuppliedByRow
                    ? t('Finance.PlanLineExplain.AccountEnteredOnRow', {
                        defaultValue: 'Account: {{label}} (entered on the row)',
                        label: data.accountLabel,
                      })
                    : t('Finance.PlanLineExplain.AccountDerivedByRule', {
                        defaultValue: 'Account: {{label}} (derived by rule)',
                        label: data.accountLabel,
                      })}
                </Typography>
              )}
            </Box>

            {!data.matchesStoredSet && (
              <Alert
                severity="warning"
                action={
                  canEdit ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => recompute.mutate()}
                      disabled={recompute.isPending}
                    >
                      {recompute.isPending
                        ? t('Finance.PlanLineExplain.Recomputing', { defaultValue: 'Recomputing…' })
                        : t('Finance.PlanLineExplain.Recompute', { defaultValue: 'Recompute' })}
                    </Button>
                  ) : undefined
                }
              >
                {t('Finance.PlanLineExplain.DriftWarning', {
                  defaultValue:
                    'The configuration has changed since this line was saved — a save today would resolve a ' +
                    'different dimension set (see below). Run Recompute to bring the line up to date.',
                })}
              </Alert>
            )}
            {recompute.isError && (
              <Alert severity="error">
                {t('Finance.PlanLineExplain.RecomputeFailed', {
                  defaultValue: 'Recompute failed — the line was not changed.',
                })}
              </Alert>
            )}
            {recompute.isSuccess && data.matchesStoredSet && (
              <Alert severity="success">
                {t('Finance.PlanLineExplain.RecomputeSuccess', {
                  defaultValue:
                    'The line was recomputed and now matches the current configuration. Refresh the list to see the updated values.',
                })}
              </Alert>
            )}

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('Finance.PlanLineExplain.ColDimension', { defaultValue: 'Dimension' })}</TableCell>
                  <TableCell>{t('Finance.PlanLineExplain.ColValue', { defaultValue: 'Value' })}</TableCell>
                  <TableCell>{t('Finance.PlanLineExplain.ColSource', { defaultValue: 'Source' })}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.axes.map((axis) => (
                  <TableRow key={axis.dimensionDefinitionId} sx={{ verticalAlign: 'top' }}>
                    <TableCell>
                      <Tooltip title={axis.definitionCode}>
                        <span>{axis.definitionName}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {axis.winner ? valueText(t, axis.winner) : '—'}
                      {axis.overridden.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          {/* Already ordered strongest-rung-first by the server. */}
                          {axis.overridden.map((o) => (
                            <Typography
                              key={`${o.dimensionValueId}-${o.source}-${o.carrier ?? ''}`}
                              variant="caption"
                              color="text.disabled"
                              sx={{ display: 'block' }}
                            >
                              <span style={{ textDecoration: 'line-through' }}>{valueText(t, o)}</span>{' '}
                              ({sourceText(t, o, ladder)})
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {axis.winner && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {rungByRank(ladder, axis.winner.precedenceRank) && (
                            <StepBadge step={rungByRank(ladder, axis.winner.precedenceRank)!.step} />
                          )}
                          <Typography variant="body2">{sourceText(t, axis.winner, ladder)}</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <OutcomeChip outcome={axis.outcome} />
                    </TableCell>
                  </TableRow>
                ))}
                {data.axes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        {t('Finance.PlanLineExplain.Empty', {
                          defaultValue: 'No dimension candidates — the line carries no dimensions.',
                        })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {!!ladder?.length && <PrecedenceLegend ladder={ladder} usedRanks={usedRanks} />}

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t('Finance.PlanLineExplain.StoredSet', {
                  defaultValue: 'Stored set: {{value}}',
                  value: data.storedDisplayValue || none,
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t('Finance.PlanLineExplain.ResolvesNow', {
                  defaultValue: 'Resolves now: {{value}}',
                  value: data.resolvedDisplayValue || none,
                })}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close', { defaultValue: 'Close' })}</Button>
      </DialogActions>
    </Dialog>
  );
}
